import type {
  FocusEventHandler,
  FunctionComponent,
  InputHTMLAttributes,
} from "react";
import React, { useEffect, useRef, useState } from "react";

import { Field } from "@flywire/headlessui-tailwindcss";
import { useMoneyInput } from "@flywire/react-headlessui";

import { VisuallyHidden } from "components/ui/VisuallyHidden/VisuallyHidden";
import { useTranslations } from "utils/translations/useTranslations";

type MoneyInputProps = {
  currencySymbol?: string;
  decimalMark?: string;
  error?: string | boolean;
  hint?: string;
  label?: string;
  name: string;
  onBlur?: (name: string, value: number) => void;
  onChange?: (name: string, value: number) => void;
  subunitToUnit?: number;
  symbolFirst?: boolean;
  thousandsSeparator?: string;
  value?: number;
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onBlur" | "onChange" | "value" | "name"
>;

const MoneyInput: FunctionComponent<MoneyInputProps> = ({
  autoComplete = "off",
  className,
  currencySymbol = "$",
  decimalMark = ".",
  disabled = false,
  error,
  hint,
  label,
  maxLength = 10,
  name,
  id = name,
  onBlur,
  onChange,
  placeholder = "0.0",
  readOnly = false,
  required,
  subunitToUnit = 100,
  symbolFirst = true,
  thousandsSeparator = ",",
  value,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [amount, setAmount] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const isEnabled = !disabled && !readOnly;
  const isInvalid = typeof error === "string" && error !== undefined;

  const toCents = (value: number) => {
    const decimals = subunitToUnit > 0 ? Math.log10(subunitToUnit) : 0;
    return Number.parseInt((value * subunitToUnit).toFixed(decimals));
  };

  const fromCents = (value?: number) => {
    return value !== undefined ? value / subunitToUnit : undefined;
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = (evt) => {
    if (isEnabled) {
      const {
        target,
        target: { value },
      } = evt;

      target.setSelectionRange(0, value.length);
      setIsFocused(true);
    }
  };

  const handleValueChange = (value: number) => {
    onChange?.(name, toCents(value));
  };

  const handleValueBlur = (value: number) => {
    const valueInCents = toCents(value);
    setIsFocused(false);
    setAmount(valueInCents);
    onBlur?.(name, valueInCents);
  };

  const { inputProps } = useMoneyInput({
    ...props,
    autoComplete,
    decimalMark,
    disabled,
    required,
    thousandsSeparator,
    subunitToUnit,
    initialValue: fromCents(amount),
    onFocus: handleFocus,
    onValueBlur: handleValueBlur,
    onValueChange: handleValueChange,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: isFocused
  useEffect(() => {
    !isFocused && amount !== 0 && setAmount(value);
  }, [value]);

  const i18n = useTranslations();

  return (
    <Field id={id} className={className}>
      <Field.Group as="div">
        {symbolFirst && (
          <Field.Addon position="left" data-testid={`${name}-currency`}>
            {currencySymbol}
          </Field.Addon>
        )}
        <div className="relative w-full">
          <Field.Input
            data-no-outline
            data-testid={`MoneyInput-${name}`}
            id={id}
            maxLength={maxLength}
            name={name}
            placeholder={placeholder}
            position={symbolFirst ? "right" : "left"}
            readOnly={readOnly}
            {...props}
            {...inputProps}
            ref={inputRef}
          />
          {label && (
            <Field.Label id={`${id}-label`}>
              {label}
            </Field.Label>
          )}
        </div>
        {!symbolFirst && (
          <Field.Addon position="right" data-testid={`${name}-currency`}>
            {currencySymbol}
          </Field.Addon>
        )}
      </Field.Group>
      {isInvalid && (
        <Field.Error
          data-testid={`${name}-error`}
          className={error ? "visible mb-0" : "hidden"}
        >
          <VisuallyHidden as="span">{label}</VisuallyHidden>
          {error && i18n.t(error)}
        </Field.Error>
      )}
      {hint && (
        <Field.Hint data-testid={`${name}-hint`} className="mb-0 block">
          {hint}
        </Field.Hint>
      )}
    </Field>
  );
};

export { MoneyInput, MoneyInputProps };
